import React, { useState, useRef, useEffect } from "react";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { translateManeuver } from "../MapBox/MapBoxFeatures/CompileProject";
import DialogBoxWithConfirmation from "../../util/DialogBoxWithConfirmation";
import AddStepForm from "./AddStepForm";
import CustomStep from "./CustomStep";
import EditStepForm from "./EditStepForm";
import CustomNavigationOptions from "./CustomNavigationOptions";
import { extractNumericDistance } from "../../util/Util";
import { importantManeuvers } from "../GPX/Resources";
import "../../style/DirectionsContainer.css";

const formatStep = (step, index) => {
  const location =
    step.start_location && typeof step.start_location.toUrlValue === "function"
      ? step.start_location.toUrlValue(6)
      : typeof step.start_location === "string"
      ? step.start_location
      : "";

  return {
    stepNumber: index + 1,
    instructions: step.instructions ?? "",
    distance: step.distance ?? { text: "", value: 0 },
    end_location: step.end_location ?? null,
    maneuver: step.maneuver ?? "",
    start_location: location,
    lat: step.start_location,
    lng: step.start_location,
    distanceIsModified: false,
    stepIsModified: step.stepIsModified ?? false,
    stepIsAdded: step.stepIsAdded ?? false,
    checkAudio: step.checkAudio ?? true,
  };
};

const extractIndications = (
  customIndicationsEdited,
  customNavigationPoints,
  directionsResponse,
  selectedRoute
) => {
  if (customIndicationsEdited) return customNavigationPoints;

  const steps = [];
  directionsResponse?.routes?.[selectedRoute]?.legs?.forEach((leg) => {
    leg.steps.forEach((step) => {
      if (importantManeuvers.includes(step.maneuver)) {
        steps.push(step);
      }
    });
  });
  return steps;
};

export const CustomNavigation = ({
  directionsResponse,
  selectedRoute,
  customIndicationsEdited,
  setCustomIndicationsEdited,
  customNavigationPoints,
  setCustomNavigationPoints,
  setMapPosition,
  setInfoCustomNavig,
  kmlFile,
}) => {
  const { t } = useTranslation();
  const divRef = useRef(null);

  const [editStep, setEditStep] = useState();
  const [steps, setSteps] = useState([]);
  const [options, setOptions] = useState([]);
  const [displayType, toggleType] = useState(true);
  const [displayCoordinates, toggleCoordinates] = useState(true);
  const [checkAudio, setCheckAudio] = useState();
  const [firstStep, setFirstStep] = useState(false);

  const [newStep, setNewStep] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [newStepDistance, setNewStepDistance] = useState({
    value: "",
    text: "",
  });
  const [previousDistance, setPreviousDistance] = useState("");
  const [newStepType, setNewStepType] = useState("");
  const [newStepCoordinates, setNewStepCoordinates] = useState("");

  const clearFormState = () => {
    setNewStep(false);
    setNewMessage("");
    setNewStepDistance({ value: "", text: "" });
    setNewStepType("");
    setNewStepCoordinates("");
    setCheckAudio();
  };

  useEffect(() => {
    if ((!directionsResponse || kmlFile) && !customIndicationsEdited) return;

    const indications = extractIndications(
      customIndicationsEdited,
      customNavigationPoints,
      directionsResponse,
      selectedRoute
    );
    const formatted = indications.map(formatStep);

    const maneuvers = formatted.map((step) =>
      translateManeuver(step.maneuver, step.instructions)
    );
    setOptions([...new Set(maneuvers)]);

    setSteps(formatted);
    setCustomNavigationPoints(formatted);
  }, [directionsResponse, selectedRoute]);

  const handleStepClick = (step) => {
    if (
      typeof step.start_location === "string" &&
      step.start_location.includes(",")
    ) {
      const [lat, lng] = step.start_location.split(",");
      setMapPosition({ lat: parseFloat(lat), lng: parseFloat(lng) });
      setInfoCustomNavig(parse(step.instructions));
    }
  };

  const handleEdit = () => {
    const newSteps = steps.map((step) =>
      step.stepNumber === editStep.stepNumber
        ? {
            ...step,
            instructions: newMessage || step.instructions,
            distance: newStepDistance.text
              ? {
                  text: /(?=.*(?:m|km|-1))/i.test(newStepDistance.text)
                    ? newStepDistance.text
                    : newStepDistance.text + " m",
                  value: extractNumericDistance(newStepDistance.value),
                }
              : step.distance,
            maneuver: newStepType || step.maneuver,
            start_location: newStepCoordinates || step.start_location,
            stepIsModified:
              (newMessage && newMessage !== step.instructions) ||
              (newStepDistance.text &&
                newStepDistance.text !== step.distance.text) ||
              (newStepType && newStepType !== step.maneuver) ||
              (newStepCoordinates && newStepCoordinates !== step.start_location)
                ? true
                : step.stepIsModified ?? false,
            checkAudio:
              typeof checkAudio === "boolean" ? checkAudio : step.checkAudio,
          }
        : step
    );

    setSteps(newSteps);
    setCustomNavigationPoints(newSteps);
    setEditStep(null);
    clearFormState();
    setCustomIndicationsEdited(true);
  };

  const handleDeleteStep = (step) => {
    const updatedSteps = steps
      .filter((s) => s.stepNumber !== step.stepNumber)
      .map((s, i) => ({ ...s, stepNumber: i + 1 }));
    setSteps(updatedSteps);
    setCustomNavigationPoints(updatedSteps);
    setCustomIndicationsEdited(true);
  };

  const resetCustomIndications = async () => {
    const confirm = await DialogBoxWithConfirmation({
      title: t("confirmation"),
      text: t("resetIndications"),
      icon: "question",
      confirmButtonText: t("yes"),
      cancelButtonText: t("no"),
    });
    if (!confirm) return;

    const steps = extractIndications(
      false,
      [],
      directionsResponse,
      selectedRoute
    ).map(formatStep);
    setSteps(steps);
    setCustomNavigationPoints(steps);
    clearFormState();
    setCustomIndicationsEdited(false);
  };

  const handleAddNewStep = () => {
    const index = newStep.stepNumber;
    const newSteps = [...steps];
    newSteps.splice(index, 0, {
      stepNumber: firstStep ? 1 : index + 1,
      instructions: newMessage,
      distance: {
        text: /(?=.*(?:m|km|-1))/i.test(newStepDistance.text)
          ? newStepDistance.text
          : newStepDistance.text + " m",
        value: extractNumericDistance(newStepDistance.value),
      },
      maneuver: newStepType,
      start_location: newStepCoordinates,
      stepIsAdded: true,
      distanceIsModified: true,
      checkAudio: true,
    });
    const updatedSteps = newSteps.map((s, i) => ({ ...s, stepNumber: i + 1 }));
    setSteps(updatedSteps);
    setCustomNavigationPoints(updatedSteps);
    clearFormState();
    setCustomIndicationsEdited(true);
  };

  return (
    <div style={{ height: "500px" }}>
      <CustomNavigationOptions
        customIndicationsEdited={customIndicationsEdited}
        toggleType={toggleType}
        displayType={displayType}
        toggleCoordinates={toggleCoordinates}
        displayCoordinates={displayCoordinates}
        resetCustomIndications={resetCustomIndications}
        steps={steps}
        setFirstStep={setFirstStep}
      />

      {firstStep && (
        <AddStepForm
          handleSubmit={(e) => {
            e.preventDefault();
            handleAddNewStep();
            setFirstStep(false);
          }}
          handleCancel={() => setFirstStep(false)}
          setNewMessage={setNewMessage}
          setNewStepDistance={setNewStepDistance}
          setNewStepType={setNewStepType}
          setNewStepCoordinates={setNewStepCoordinates}
          checkAudio={checkAudio}
          setCheckAudio={setCheckAudio}
        />
      )}

      <ul style={{ height: "500px", overflowY: "auto", listStyleType: "none" }}>
        {steps.map((step) => (
          <li
            key={step.stepNumber}
            onClick={() => handleStepClick(step)}
            data-testid="showSteps"
            className="stepContainer mb-2"
          >
            {editStep?.stepNumber === step.stepNumber ? (
              <EditStepForm
                step={step}
                divRef={divRef}
                newMessage={newMessage}
                newStepType={newStepType}
                checkAudio={checkAudio}
                setCheckAudio={setCheckAudio}
                setNewMessage={setNewMessage}
                setNewStepType={setNewStepType}
                newStepDistance={newStepDistance}
                setNewStepCoordinates={setNewStepCoordinates}
                handleEdit={handleEdit}
                stopEdit={() => setEditStep(null)}
                updateStepDistance={(e) => {
                  const val = e.target.value;
                  setPreviousDistance(val);
                  setNewStepDistance({ value: val, text: val });
                }}
                distanceIsModified={step.isValidate}
                setDistanceIsModified={(val) =>
                  setSteps(
                    steps.map((s) =>
                      s.stepNumber === step.stepNumber
                        ? { ...s, distanceIsModified: val }
                        : s
                    )
                  )
                }
              />
            ) : (
              <CustomStep
                step={step}
                displayCoordinates={displayCoordinates}
                displayType={displayType}
                handleDeleteStep={() => handleDeleteStep(step)}
                setNewStep={() => setNewStep(step)}
                handleEditStep={() => setEditStep(step)}
              />
            )}

            {newStep?.stepNumber === step.stepNumber && (
              <AddStepForm
                handleSubmit={(e) => handleAddNewStep(e, step)}
                handleCancel={() => setNewStep(false)}
                setNewMessage={setNewMessage}
                setNewStepDistance={setNewStepDistance}
                setNewStepType={setNewStepType}
                setNewStepCoordinates={setNewStepCoordinates}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CustomNavigation;
